import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";

/**
 * Self-service password reset · step 1
 *
 * Always shows the same confirmation message regardless of whether the email
 * matches a real account, so attackers can't enumerate registered users.
 * Pre-fills the email from `?email=` so welcome emails can drop students
 * straight into the form.
 */
export default function ForgotPassword() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (er) {
      setErr(formatApiError(er.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="forgot-password-page">
      <SEO noindex title="Reset your password" description="Request a password reset link." />
      <div className="container-aviana max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-8">
          <ArrowLeft size={14} /> Back to sign in
        </Link>

        {!submitted ? (
          <>
            <p className="overline mb-3">Forgot password</p>
            <h1 className="font-serif text-5xl text-aviana-ink leading-tight">Let's get you back in.</h1>
            <p className="mt-3 text-aviana-muted">Enter the email on your account and we'll send you a secure reset link. It's valid for <strong className="text-aviana-ink">30 minutes</strong>.</p>

            <form onSubmit={submit} className="mt-10 space-y-6" data-testid="forgot-password-form">
              <div>
                <label className="overline">Email</label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-transparent border-b border-aviana-border focus:border-aviana-gold py-3 outline-none"
                  data-testid="forgot-password-email"
                />
              </div>
              {err && <p className="text-sm text-red-700" data-testid="forgot-password-error">{err}</p>}
              <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="forgot-password-submit">
                {busy ? "Sending link…" : "Send reset link"}
              </button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-password-submitted">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-aviana-gold/15 text-aviana-goldDeep mb-6">
              <CheckCircle2 size={26} strokeWidth={1.5} />
            </div>
            <p className="overline mb-3">Check your inbox</p>
            <h1 className="font-serif text-5xl text-aviana-ink leading-tight">It's on the way.</h1>
            <p className="mt-4 text-aviana-inkSoft leading-[1.7]">
              If <strong className="text-aviana-ink">{email}</strong> matches an account, we've sent a reset link from <em>noreply@avianayoga.com</em>. Please check your inbox — and your spam folder, just in case.
            </p>
            <p className="mt-4 text-aviana-muted text-[14px] leading-[1.7] flex items-start gap-2">
              <Mail size={16} className="text-aviana-plum mt-0.5 flex-shrink-0" />
              <span>The link expires in 30 minutes. If it does, just come back here and request a new one.</span>
            </p>
            <Link
              to="/login"
              className="mt-8 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink"
              data-testid="back-to-login-link"
            >
              Back to sign in →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
